import { log } from "./log.js";
/**
 * session-store.ts — persist editor state across in-tab navigation and
 * full-tab reloads.
 *
 * WHY: the editor's working state lives only in JS state; navigating
 * away and back via the browser history (DocAble link → back button)
 * unmounts the SPA and creates a fresh editor instance with no file
 * loaded, no IR rebuilt, and no in-flight op log. Without this module,
 * the user has to re-pick the file and lose any unsynced edits every
 * time they navigate away.
 *
 * Two responsibilities, one IDB database (`adatool-editor`):
 *
 *   1. **PDF bytes** (legacy v1 store `openFile`, single record at key
 *      `current`). Tab-scoped pointer in `sessionStorage`. Unchanged
 *      role since v1.
 *
 *   2. **Op-log + IR snapshot** (NEW v2 stores `oplog` and `ir`). Per
 *      The
 *      `oplog` store is keyed by `[jobId, sessionId, opSeq]` and holds
 *      every op the user performs, with a local `synced` flag indicating
 *      whether the server-side oplog has it. The `ir` store is keyed by
 *      `jobId` and holds the most recent IR snapshot for fast boot.
 *
 * Schema migration v1 → v2 preserves the existing v1 record (PDF bytes
 * keyed by "current") via a non-destructive `onupgradeneeded` block.
 *
 * Public API:
 *   storeOpenFile(file)        — Promise<void>; persist file bytes + meta.
 *   loadOpenFile()             — Promise<File|null>; reconstruct File or null.
 *   clearOpenFile()            — Promise<void>; clear v1 file record.
 *   openEditorDb()             — Promise<IDBDatabase>; opened with v2 schema.
 *                                Callers in `idb-oplog.js` use this.
 *
 * Tested by `test/session-store-v2.test.ts` (schema migration) and
 * `test/idb-oplog.test.ts` (op-log logic).
 */
const META_KEY = "adatool.editor.openFile";
const DB_NAME = "adatool-editor";
const DB_VERSION = 2;
const FILE_STORE = "openFile";
const OPLOG_STORE = "oplog";
const IR_STORE = "ir";
const RECORD_KEY = "current"; // single-record key for the v1 file store
// ─── IndexedDB helpers ────────────────────────────────────────────────────────
/**
 * Open (or create) the IndexedDB store with the v2 schema. Resolves
 * with the IDBDatabase. The upgrade handler is non-destructive: an
 * existing v1 `openFile` record is preserved, and the two new v2 stores
 * (`oplog`, `ir`) are added alongside.
 */
export function openEditorDb() {
    return new Promise((resolve, reject) => {
        if (typeof indexedDB === "undefined") {
            reject(new Error("IndexedDB is not available in this environment"));
            return;
        }
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = (ev) => {
            const db = req.result;
            const oldVersion = ev.oldVersion || 0;
            // v0 → v1 (cold-create the v1 store; preserves prior behavior).
            if (!db.objectStoreNames.contains(FILE_STORE)) {
                db.createObjectStore(FILE_STORE);
            }
            // v1 → v2: add oplog + ir stores. Existing FILE_STORE record
            // (if any) is untouched — IDB upgrades preserve data unless
            // an explicit deleteObjectStore is called.
            if (oldVersion < 2) {
                if (!db.objectStoreNames.contains(OPLOG_STORE)) {
                    const opStore = db.createObjectStore(OPLOG_STORE, {
                        keyPath: ["jobId", "sessionId", "opSeq"],
                    });
                    opStore.createIndex("byJob", "jobId", { unique: false });
                }
                if (!db.objectStoreNames.contains(IR_STORE)) {
                    db.createObjectStore(IR_STORE, { keyPath: "jobId" });
                }
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error || new Error("indexedDB open failed"));
        // `onblocked` fires if another tab holds an older version open;
        // resolve with reject so the caller can fall back gracefully.
        req.onblocked = () => reject(new Error("indexedDB upgrade blocked by another tab"));
    });
}
// Backwards-compat alias (internal).
function openDb() { return openEditorDb(); }
/**
 * Run an IDB transaction returning a promise for the request result.
 * Scoped to the v1 file store; do not reuse for the v2 stores.
 */
async function withFileStore(mode, fn) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(FILE_STORE, mode);
        const store = tx.objectStore(FILE_STORE);
        const req = fn(store);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error || new Error("IDB request failed"));
        tx.oncomplete = () => db.close();
        tx.onabort = () => db.close();
    });
}
// ─── Public API: PDF-bytes (legacy v1 store) ─────────────────────────────────
/**
 * Persist a File to IDB + write metadata into sessionStorage.
 */
export async function storeOpenFile(file) {
    if (!file || typeof file.arrayBuffer !== "function") {
        throw new TypeError("storeOpenFile: expected a File-like object");
    }
    const buf = await file.arrayBuffer();
    try {
        await withFileStore("readwrite", (store) => store.put(buf, RECORD_KEY));
    }
    catch (err) {
        // Best-effort: if IDB is unavailable, skip persistence rather than
        // failing the load. The user just won't get back-button restoration.
        log.warn("[session-store] IDB write failed:", err);
        return;
    }
    const meta = {
        name: file.name || "document.pdf",
        type: file.type || "application/pdf",
        size: file.size ?? 0,
        lastModified: file.lastModified || Date.now(),
    };
    try {
        if (typeof sessionStorage !== "undefined") {
            sessionStorage.setItem(META_KEY, JSON.stringify(meta));
        }
    }
    catch {
        // best-effort; storage may be unavailable in private mode
    }
}
/**
 * Load any previously-stored File. Returns null if nothing was stored or
 * the storage is unavailable.
 */
export async function loadOpenFile() {
    let meta = null;
    try {
        if (typeof sessionStorage === "undefined")
            return null;
        const raw = sessionStorage.getItem(META_KEY);
        if (!raw)
            return null;
        meta = JSON.parse(raw);
    }
    catch {
        return null;
    }
    if (!meta || !meta.name)
        return null;
    let buf;
    try {
        buf = await withFileStore("readonly", (store) => store.get(RECORD_KEY));
    }
    catch {
        return null;
    }
    if (!buf)
        return null;
    // Reconstruct a File. Use Blob constructor for environments where the
    // File constructor isn't directly invocable (older Safari).
    try {
        return new File([buf], meta.name, {
            type: meta.type || "application/pdf",
            lastModified: meta.lastModified || Date.now(),
        });
    }
    catch {
        // Fallback: a Blob masquerading as a File-ish object.
        const blob = new Blob([buf], { type: meta.type || "application/pdf" });
        // WHY: Blob with manual File-ish properties is the intentional fallback
        // when the File constructor is unavailable (older Safari). Safe: all callers
        // use only .name, .lastModified, .arrayBuffer(), which Blob provides.
        blob.name = meta.name;
        blob.lastModified = meta.lastModified || Date.now();
        return blob;
    }
}
/**
 * Clear the persisted file (called from explicit close / new-file actions).
 * Does NOT clear the v2 oplog / ir stores — those are per-job and
 * survive file changes.
 */
export async function clearOpenFile() {
    try {
        if (typeof sessionStorage !== "undefined") {
            sessionStorage.removeItem(META_KEY);
        }
    }
    catch {
        // best-effort
    }
    try {
        await withFileStore("readwrite", (store) => store.delete(RECORD_KEY));
    }
    catch {
        // best-effort
    }
}
// Exposed for tests.
export const __internals = {
    META_KEY, DB_NAME, DB_VERSION, FILE_STORE, OPLOG_STORE, IR_STORE, RECORD_KEY,
};
